import {getFormProps, getInputProps, useForm} from '@conform-to/react'
import {type ActionFunctionArgs, json, type MetaFunction} from '@remix-run/node'

import {HoneypotInputs} from 'remix-utils/honeypot/react'
import {ErrorBulletList, Field, PasswordField} from '#app/components/forms/forms.tsx'
import {StatusButton} from '#app/components/forms/status-button.tsx'
import {ResetPasswordSchemaNoConfirmation} from '#app/utils/user-validation.ts'
import {getZodConstraint, parseWithZod} from '@conform-to/zod'
import {Form, useActionData} from '@remix-run/react'
import {useIsPending} from '#app/utils/misc.tsx'
import {postAndReturnStatus} from '#app/utils/refundr.server.aragorn.ts'
import {z} from 'zod'
import {type PasswordInput} from '#app/model/model.ts'

import {checkHoneypot} from '#app/utils/honeypot.server.ts'
import {useState} from 'react'
import {redirectWithToast} from '#app/utils/toast.server.ts';

export const meta: MetaFunction = () => {
	return [{ title: 'Reset Password | refundr' }]
}

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()

	// Honeypot is a simple technique to prevent spam bots from submitting forms. It works by adding a hidden field to the form that bots will fill, but humans won't.
	checkHoneypot(formData)

	const submission = await parseWithZod(formData, {
		schema: ResetPasswordSchemaNoConfirmation.superRefine(async (data, ctx) => {
			const response = await postAndReturnStatus<PasswordInput>(request, '/aws/reset-password/', {
				email: data.email,
				password: data.password,
				code: data.code,
			}, true)
			if (!response?.success && 400 === response?.statusCode) {
				ctx.addIssue({
					code: z.ZodIssueCode.custom,
					message: response.data,
				})
				return z.NEVER
			}
			return { ...data, response: response.data }
		}),
		async: true,
	})

	if (submission.status !== 'success') {
		return json(
			{ result: submission.reply({ hideFields: ['password'] }) },
			{ status: submission.status === 'error' ? 400 : 200 },
		)
	}

	return redirectWithToast('/login', {
		description: 'You have successfully reset your password. Use the new password to login.',
		type: 'success',
		title: 'Success'
	})
}

export default function ForgotPasswordRoute() {
	const actionData = useActionData<typeof action>()
	const isPending = useIsPending()
	const [showPassword, setShowPassword] = useState(false)

	const togglePasswordVisibility = () => {
		setShowPassword(prev => !prev)
	}

	const [form, fields] = useForm({
		id: 'reset-password',
		constraint: getZodConstraint(ResetPasswordSchemaNoConfirmation),
		// @ts-ignore
		lastResult: actionData?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ResetPasswordSchemaNoConfirmation })
		},
		shouldRevalidate: 'onBlur',
	})

	return (
		<div className="flex min-h-screen flex-col md:flex-row">
			{/* Left: Marketing Section */}
			<div className="dark-background order-last flex w-full items-center justify-center p-6 text-black md:order-none md:w-1/2">
				<div className="mt-5 flex flex-col items-center justify-center space-y-4">
					<div>
						<img className="block h-12 w-auto" src="/img/logo/refundr-white.png" alt="refundr" />
					</div>
					<div className="text-center font-mono text-gray-200">
						Re-inventing how you connect with your Tax Clients.
					</div>
					<div className="pt-4">
						<img className="block w-auto" src="/img/tax-return-card-md.png" alt="refundr" />
					</div>
				</div>
			</div>

			{/* Right: Login Section */}
			<div className="flex w-full items-center justify-center bg-white p-6 md:w-1/2">
				<div className="mb-16 mt-5 w-full max-w-md bg-white">
					<div className="mb-6 flex items-center justify-center">
						<div className="text-2xl font-bold text-gray-600">Reset Password</div>
					</div>
					<div className="mb-6 flex items-center justify-center">
						<div className="mt-3 text-body-sm text-muted-foreground">
							We've sent a code to your email which is required to create your new password.
						</div>
					</div>
					<div>
						<Form method="POST" {...getFormProps(form)}>
							<ErrorBulletList errors={form.errors} id={form.errorId} />

							<HoneypotInputs />
							<Field
								labelProps={{
									htmlFor: fields.email.id,
									children: 'email',
									className: 'text-gray-700',
								}}
								inputProps={{
									...getInputProps(fields.email, { type: 'email' }),
									autoFocus: true,
									autoComplete: 'email',
									className: 'h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
									placeholder: 'enter your email',
								}}
								errors={fields.email.errors}
							/>
							<Field
								labelProps={{
									htmlFor: fields.code.id,
									children: 'verification code',
									className: 'text-gray-700',
								}}
								inputProps={{
									...getInputProps(fields.code, { type: 'text' }),
									autoComplete: 'code',
									autoFocus: true,
									className: 'lowercase h-14 w-full rounded-lg bg-gray-100 px-3 py-2 text-black shadow border-gray-300',
									placeholder: 'enter verification code',
								}}
								errors={fields.code.errors}
							/>
							<PasswordField
								className="w-full"
								createPassword={true}
								showPassword={showPassword}
								togglePasswordVisibility={togglePasswordVisibility}
								inputProps={{
									...getInputProps(fields.password, {type: 'password'}),
									autoComplete: 'current-password',
									placeholder: 'Enter your new password',
								}}
								errors={fields.password.errors}
							/>
							<StatusButton
								className="h-12 w-full rounded-md bg-blue-500 px-4 py-2 text-white hover:bg-blue-600"
								status={isPending ? 'pending' : form.status ?? 'idle'}
								type="submit"
								disabled={isPending}
							>
								Reset password
							</StatusButton>
						</Form>
					</div>
				</div>
			</div>
		</div>
	)
}
